import { unifiedApi } from "@/common";
import { handleApiError } from "@/common/handleApiError";
import { useUserStore } from "@/stores/user";
import { AxiosError } from "axios";
import { emailValidationErrors, openTextBrands } from "../Buy/BuyEnums";
import { executeReCaptcha } from "@/common/ReCaptchaV3";
import router from "@/routes";
import { useAolAccountStore } from "@/stores/AolAccount";
import { HTTP_STATUS_NOT_ACCEPTABLE } from "@/define";
import { logEvent, logException } from "@/common/logger";
import { useNotificationsStore } from "@/stores/notifications";
import { getRedirectToLoginURLWithEmail } from "@/common/loginRedirect";
import { aolErrors, aolTokenStatus } from "@/globalEnums";
import { getUrl } from "@/common/getUrl";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { IErrorMessage } from "@/common/api/interfaces";

export async function validateAolToken(route) {
  try {
    const sku = route.query.sku;
    const token = route.query.token;
    const notificationsStore = useNotificationsStore();
    const aolAccountStore = useAolAccountStore();

    if (sku && token) {
      const tokenStatus = (await unifiedApi.validateAolToken({ sku: sku.toString(), token: token.toString() })).data;
      if (tokenStatus === aolTokenStatus.VALID.toString()) {
        aolAccountStore.token = token;
        aolAccountStore.sku = sku;
        return true;
      } else if (tokenStatus === aolTokenStatus.USED.toString()) {
        notificationsStore.addNotification({ type: "SubscriptionAlreadyActive" });
        await router.push("error/tokenAlreadyInUse");
        return false;
      } else if (tokenStatus === aolTokenStatus.INVALID.toString()) {
        await router.push("error/tokenApi");
        return false;
      }
    } else {
      notificationsStore.addNotification({ type: "ThereWasAProblemOnOurEnd" });
      await router.push("error/tokenApi");
      return false;
    }
  } catch (err) {
    logException(err as AxiosError, "validateAolToken api failed");
    await router.push("error/tokenApi");
  }
}

export async function checkForUserAccount(
  email,
  showV2Recaptcha,
  captchaV2ResponseToken,
  recaptchaInstance,
  componentName
) {
  //Don't run for a blank email or email with errors

  if (!email.value) {
    return showV2Recaptcha;
  }

  const userStore = useUserStore();
  const aolAccountStore = useAolAccountStore();
  const notificationsStore = useNotificationsStore();

  notificationsStore.clearNotifications();

  try {
    let v2Token = "";
    let v3Token = "";

    //if we're showing the v2 recaptcha, then send its token (second attempt)
    //otherwise, send the v3 token (first attempt)
    if (showV2Recaptcha.value) {
      v2Token = captchaV2ResponseToken.value;
    } else if (recaptchaInstance.value) {
      v3Token = await executeReCaptcha(recaptchaInstance.value);
    }
    //This will create an Account if the email does not exist
    //It will return 400 if the email is for an SMB Account
    aolAccountStore.createAccountResponse = (
      await unifiedApi.checkExistingEmail({
        email: email.value,
        preferredLanguage: userStore.selectedLanguage,
        recaptchaV2Response: v2Token,
        recaptchaV3Response: v3Token,
        brand: openTextBrands.Carbonite,
        isAol: true,
      })
    ).data;

    if (aolAccountStore.createAccountResponse.isExistingUser) {
      redirectToAolLogin(email.value);
    } else {
      await router.push("/aol/addPassword");
    }
    aolAccountStore.resetResponses();
  } catch (error) {
    logException(error as Error, `${componentName}: AolAccountCheck`);
    const errorResponse = error as AxiosError;

    if ((errorResponse.response?.data as IErrorMessage)?.message === aolErrors.IsNonEmptySafeAccount) {
      notificationsStore.addNotification({ type: "EmailAlreadyInUseError" });
      aolAccountStore.isNonEmptyAccount = true;
      return;
    }

    aolAccountStore.errorResponseStatus = errorResponse.response?.status;
    aolAccountStore.errorResponseMessage = (errorResponse.response?.data as Error).message;
    if (aolAccountStore.errorResponseMessage == emailValidationErrors.EmailInUseNoLoginLink) {
      aolAccountStore.isSmbEmail = true;
      notificationsStore.addNotification({ type: "SmbAccountExistsForEmail" });
    }
  }
  return await handleRecaptchaAndDetermineV2Status(aolAccountStore, showV2Recaptcha);
}

async function handleRecaptchaAndDetermineV2Status(aolAccountStore, showV2Recaptcha) {
  const notificationsStore = useNotificationsStore();
  if (aolAccountStore.errorResponseStatus) {
    //recaptcha failed
    if (aolAccountStore.errorResponseStatus == HTTP_STATUS_NOT_ACCEPTABLE) {
      if (!showV2Recaptcha.value) {
        //if we failed v3, then show v2
        showV2Recaptcha.value = true;
      } else {
        //if we failed v2, then notify the user -- this is the end of the line
        notificationsStore.addNotification({ type: "RecaptchaValidation_invalid" });
        showV2Recaptcha.value = false;
      }
    }
  }
  return showV2Recaptcha.value;
}

function redirectToAolLogin(email) {
  const aolAccountStore = useAolAccountStore();
  const homeDomain = new URL(window.location.origin);
  const redirectUrl = `${homeDomain.origin}/aol/preparing?token=${aolAccountStore.token}&sku=${aolAccountStore.sku}`;
  window.location.href = getRedirectToLoginURLWithEmail(redirectUrl, email);
}

export async function addPasswordAol(userGuid, accountPassword, componentName) {
  const notificationsStore = useNotificationsStore();
  try {
    if (accountPassword) {
      const response = (await unifiedApi.setNewAccountPassword(userGuid, accountPassword)).data;
      if (!response || !response.success) {
        logEvent("SetPasswordFailure", `${componentName}`);
        notificationsStore.clearNotifications();
        notificationsStore.addNotification({ type: "NewAccountPasswordError" });
      } else {
        router.push("/aol/preparing");
      }
    }
  } catch (error) {
    logEvent("NewAccountPasswordError", `${componentName}`);
    handleApiError(error as AxiosError, true, "NewAccountPasswordError");
  }
}

export function openAolSupportPage() {
  window.open(getUrl("AOL_SUPPORT_URL"), "_blank", "noopener,noreferrer");
}

export async function openAolSubscriptionsPage(isRenewal) {
  const url = isRenewal ? getUrl("AOL_RENEW_SUBSCRIPTIONS_URL") : getUrl("AOL_SUBSCRIPTIONS_URL");
  window.open(url, "_blank", "noopener,noreferrer");
}

export async function createAolSubscription(token: string, sku: string, componentName: string) {
  const subscriptionsStore = useSubscriptionsStore();
  const aolAccountStore = useAolAccountStore();

  try {
    const newSubscriptionNumber = (await unifiedApi.createAolSubscription({ sku: sku, token: token })).data;
    for (
      let counter = 0;
      !subscriptionsStore.subscriptions.some(s => s.zuoraSubscriptionNumber === newSubscriptionNumber);
      counter++
    ) {
      await new Promise(resolve => setTimeout(resolve, 5000)); //5 secs
      await subscriptionsStore.forceRefreshAndPopulateSubscriptions();
      //Max wait time is 6x5 = 30 seconds, after which we throw an error
      if (counter > 6) {
        logEvent("ErrorCreatingAolSubscription", `${componentName}`);
        const notificationsStore = useNotificationsStore();
        notificationsStore.addNotification({ type: "ThereWasAProblemOnOurEnd" });
        router.push("/aol/error/tokenApi");
        return;
      }
    }
    aolAccountStore.hasAddedNewAolSubscription = true;
    router.push("/backup");
  } catch (error) {
    logEvent("ErrorCreatingAolSubscription", `${componentName}`);
    handleApiError(error as AxiosError, true, "ThereWasAProblemOnOurEnd");
    router.push("/aol/error/tokenApi");
  }
}
// call a new API endpoint to orchestrate subscription creation for AOL Carbonite (https://ot-internal.saas.microfocus.com/ui/?&p=4001/25001#/entity-navigation?entityType=work_item&id=4286304
